import { useMutation } from "@apollo/client";
import { mutations } from "@bluframe/mightychroma-mightytool-graphql-schema";
import { useCallback } from "react";
const { homeVideos: { GENERATE_SCREENSHOT_METADATA_FIELD } } = mutations;
const FIELD_MAP = {
    alternativeText: "ALTERNATIVE_TEXT",
    caption: "CAPTION"
};
/**
 * normalizeOptionalValue
 *
 * @description Trims optional string values and returns undefined when empty.
 * @param {string} [value] - Optional raw string value.
 * @returns {string | undefined} - Trimmed value when present.
 *
 */
const normalizeOptionalValue = (value) => {
    const normalized = value?.trim();
    return normalized ? normalized : undefined;
};
/**
 * normalizeOptionalIdentifier
 *
 * @description Converts optional numeric/string identifiers to trimmed string identifiers.
 * @param {number | string} [value] - Optional identifier value.
 * @returns {string | undefined} - Normalized identifier string when present.
 *
 */
const normalizeOptionalIdentifier = (value) => {
    if (value === undefined || value === null) {
        return undefined;
    }
    const normalized = value.toString().trim();
    return normalized ? normalized : undefined;
};
/**
 * useRegenerateScreenshotMetadataField
 *
 * @description Generates a single screenshot metadata field via GraphQL.
 * @returns {{
 *   onRegenerateScreenshotMetadataField:
 *     (input: RegenerateScreenshotMetadataFieldInput) => Promise<string | undefined>
 * }} - Callback wrapper used by image selector flows.
 *
 */
const useRegenerateScreenshotMetadataField = () => {
    const [generateScreenshotMetadataField] = useMutation(GENERATE_SCREENSHOT_METADATA_FIELD);
    /**
     * onRegenerateScreenshotMetadataField
     *
     * @description Calls GraphQL to regenerate one screenshot metadata field.
     * @param {RegenerateScreenshotMetadataFieldInput} input - Regeneration request payload.
     * @returns {Promise<string | undefined>} - Regenerated field value when available.
     *
     */
    const onRegenerateScreenshotMetadataField = useCallback(async ({ contentId, field, id, imdbId, originalUrl, releaseDate, title }) => {
        const normalizedOriginalUrl = originalUrl.trim();
        if (!normalizedOriginalUrl) {
            return undefined;
        }
        const { data } = await generateScreenshotMetadataField({
            variables: {
                generateScreenshotMetadataFieldInput: {
                    ...(normalizeOptionalIdentifier(contentId)
                        ? { contentId: normalizeOptionalIdentifier(contentId) }
                        : {}),
                    field: FIELD_MAP[field],
                    ...(normalizeOptionalIdentifier(id)
                        ? { id: normalizeOptionalIdentifier(id) }
                        : {}),
                    ...(normalizeOptionalValue(imdbId)
                        ? { imdbId: normalizeOptionalValue(imdbId) }
                        : {}),
                    originalUrl: normalizedOriginalUrl,
                    ...(normalizeOptionalValue(releaseDate)
                        ? { releaseDate: normalizeOptionalValue(releaseDate) }
                        : {}),
                    ...(normalizeOptionalValue(title)
                        ? { title: normalizeOptionalValue(title) }
                        : {})
                }
            }
        });
        const value = data?.generateScreenshotMetadataField?.value?.trim();
        return value ? value : undefined;
    }, [generateScreenshotMetadataField]);
    return { onRegenerateScreenshotMetadataField };
};
export default useRegenerateScreenshotMetadataField;
